import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router"
import { zodValidator } from "@tanstack/zod-adapter"
import { z } from "zod"
import {
  type ICollectionGroup,
  useCollectionGroups,
  useGetCollectionsWithDetails,
} from "~/hooks/useCollections"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import {
  DemandPlanningCollectionNotFound,
  ensureCollectionExists,
  parseCollectionIdParams,
} from "~/components/collections/collectionRouteGuard"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { Module, OverviewPage } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { CollectionProvider } from "~/context/CollectionProvider"
import { first, max, min } from "lodash"
import { useStableValue } from "~/hooks/useStableValue"
import { useVariantContext, VariantProvider } from "~/context/VariantProvider"
import { useMemo } from "react"
import { CollectionType } from "~/models/collection"
import WrongCollectionRouteError from "~/components/reusable/errors/WrongCollectionRouteError"
import { ManageCollectionDialog } from "~/components/collections/dialogs/ManageCollectionDialog"
import { ChartLine, PencilSimple, Speedometer, Sparkle, TrashSimple } from "@phosphor-icons/react"
import RemoveCollectionDialog from "~/components/collections/dialogs/RemoveCollectionDialog"
import { AddVariantsToCollectionDialog } from "~/components/collections/dialogs/AddVariantsToCollectionDialog"
import { ActionButton } from "~/components/reusable/ActionButton"
import RenameCollectionGroupDialog from "~/components/collections/dialogs/RenameCollectionGroupDialog"
import { useUser } from "~/providers/userProvider"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"

const sameElements = (a: unknown[], b: unknown[]) =>
  a.length === b.length && a.every((x, i) => x === b[i])

const noosSearchSchema = z.object({
  isGroup: z.boolean().default(false),
})

enum NOOSTab {
  overview = "Overview",
  newProducts = "New Variants",
  forecastUpdates = "Forecast Updates",
}

const RouteComponent = () => {
  const { variants: allVariants } = useVariantContext()
  const rawVariants = useMemo(() => allVariants.filter((v) => v.shouldBeShown), [allVariants])
  const { data: rawCollections, isLoading } = useGetCollectionsWithDetails()
  const { data: collectionGroups, isLoading: isCollectionGroupsLoading } = useCollectionGroups()
  const { permissions } = useUser()

  const { collectionId } = Route.useParams()
  const { isGroup } = Route.useSearch()
  const { pathname } = useLocation()

  // Naming the active tab as the last breadcrumb crumb makes lastNonVariantPath (Breadcrumb.tsx)
  // store that tab's label + path, so the variant screen's back crumb reads e.g. "Forecast Updates"
  // and returns there in one click. Overview is left unchanged (keeps the collection name).
  const activeNoosTab = pathname.endsWith("/forecast-updates")
    ? NOOSTab.forecastUpdates
    : pathname.endsWith("/new-variants")
      ? NOOSTab.newProducts
      : null

  const { collections, collectionGroup, startDate, endDate, isFashion } = useMemo(() => {
    let cols: typeof rawCollections = []
    let group: ICollectionGroup | undefined

    if (isGroup) {
      group = collectionGroups?.find((cg) => cg.id === Number(collectionId))
      cols = rawCollections?.filter((c) => c.collectionGroupId === group?.id) ?? []
    } else {
      cols = rawCollections?.filter((c) => c.id === Number(collectionId)) ?? []
    }

    return {
      collections: cols,
      collectionGroup: group,
      startDate: min(cols.map((c) => c.startDate)),
      endDate: max(cols.map((c) => c.endDate)),
      isFashion: cols.length > 0 && cols.every((c) => c.collectionType === CollectionType.Mixed),
    }
  }, [rawCollections, collectionGroups, collectionId, isGroup])

  const tabs: HeaderTab[] = useMemo(
    () => [
      {
        label: NOOSTab.overview,
        path: "/demand-planning/noos/$collectionId",
        icon: <Speedometer />,
      },
      {
        label: NOOSTab.newProducts,
        icon: <Sparkle />,
        path: "/demand-planning/noos/$collectionId/new-variants",
      },
      {
        label: NOOSTab.forecastUpdates,
        icon: <ChartLine />,
        path: "/demand-planning/noos/$collectionId/forecast-updates",
      },
    ],
    [],
  )

  // Referentially stable across unrelated layout re-renders — the tabs' variant-derived
  // work (row enrichment, filtering) keys off this array's identity.
  const variantsToShow = useStableValue(
    useMemo(() => {
      const variantIdSet = new Set(collections.flatMap((c) => c.variantIds))
      return rawVariants.filter((v) => variantIdSet.has(v.variantId))
    }, [collections, rawVariants]),
    sameElements,
  )

  const recentLabel = collectionGroup?.name ?? first(collections)?.name
  useRecordRecentlyViewed(
    !isFashion && collections.length > 0 && recentLabel
      ? {
          type: "demand-plan",
          route: `/demand-planning/noos/${collectionId}${isGroup ? "?isGroup=true" : ""}`,
          label: recentLabel,
        }
      : null,
  )

  if (isLoading || isCollectionGroupsLoading) {
    return <LoadingOverlay messages={["Getting the collection ready..."]} />
  }

  if (collections.length === 0) {
    return <DemandPlanningCollectionNotFound />
  }

  if (isFashion) {
    return (
      <WrongCollectionRouteError
        collectionId={collectionId}
        isGroup={isGroup}
        targetRoute="fashion"
      />
    )
  }

  const firstCollection = first(collections)!
  const displayName = collectionGroup?.name ?? firstCollection.name
  const totalSkuCount = new Set(collections.flatMap((c) => c.skuIds)).size

  const actions = (
    <>
      {collectionGroup ? (
        <RenameCollectionGroupDialog
          groupId={collectionGroup.id}
          currentName={collectionGroup.name}
          customTrigger={
            <ActionButton
              size="sm"
              action={{
                name: "Rename group",
                icon: <PencilSimple />,
              }}
            />
          }
        />
      ) : (
        <ManageCollectionDialog
          collection={firstCollection}
          customTrigger={
            <ActionButton
              size="sm"
              action={{
                name: "Edit collection",
                icon: <PencilSimple />,
              }}
            />
          }
        />
      )}
      <RemoveCollectionDialog
        key={firstCollection.id + "-delete"}
        collectionIds={[firstCollection.id]}
        navigateToList
        trigger={
          <ActionButton
            size="sm"
            disabled={!!collectionGroup}
            action={{
              name: "Delete collection",
              icon: <TrashSimple />,
            }}
          />
        }
      />
      {totalSkuCount > 0 && (
        <AddVariantsToCollectionDialog
          collection={firstCollection}
          disabled={!!collectionGroup}
          variantIdsInCollection={firstCollection.variantIds}
          initiallyOpen={false}
        />
      )}
    </>
  )

  return (
    <VariantProvider variants={variantsToShow}>
      <CollectionProvider
        value={{ collections, collectionGroups: collectionGroups ?? [], startDate, endDate }}
      >
        <Module
          breadcrumb={
            <BreadcrumbWrapper
              items={[
                { label: "NOOS", href: "/demand-planning/noos" },
                activeNoosTab
                  ? {
                      label: displayName,
                      href: `/demand-planning/noos/${collectionId}`,
                      search: isGroup ? { isGroup: true } : undefined,
                    }
                  : { label: displayName },
                ...(activeNoosTab ? [{ label: activeNoosTab }] : []),
              ]}
            />
          }
          actions={actions}
          tabs={
            totalSkuCount > 0
              ? { items: tabs, path: "/demand-planning/noos/$collectionId" }
              : undefined
          }
        >
          {totalSkuCount > 0 ? (
            <Outlet />
          ) : (
            <OverviewPage fillContent>
              <div className="flex-1 flex flex-col gap-2 items-center justify-center">
                <img
                  src="/no_collections.png"
                  alt="No collection"
                  className="w-[220px] h-[160px]"
                />
                <p className="heading-sm">This collection is empty</p>
                <p className="paragraph-base text-fg-subtle text-center mb-4 w-[220px]">
                  Add some products to get started.
                </p>
                <AddVariantsToCollectionDialog
                  collection={firstCollection}
                  disabled={!!collectionGroup}
                  initiallyOpen={!collectionGroup && totalSkuCount === 0 && permissions.canEdit}
                  variantIdsInCollection={firstCollection.variantIds}
                />
              </div>
            </OverviewPage>
          )}
        </Module>
      </CollectionProvider>
    </VariantProvider>
  )
}

export const Route = createFileRoute("/_auth/demand-planning/noos/$collectionId/_layout")({
  params: { parse: parseCollectionIdParams },
  validateSearch: zodValidator(noosSearchSchema),
  loaderDeps: ({ search }) => ({ isGroup: search.isGroup }),
  loader: ({ params, deps, context }) =>
    ensureCollectionExists(context.trpcUtils, params.collectionId, deps.isGroup),
  pendingComponent: () => <LoadingOverlay messages={["Getting the collection ready..."]} />,
  notFoundComponent: DemandPlanningCollectionNotFound,
  component: RouteComponent,
})
